import type { Clip, GuidanceTemplate } from '@/state/types.ts';
import { db } from '@/firebase.ts';
import { ref, push, set, update, serverTimestamp } from 'firebase/database';

type EventPayload = Record<string, unknown>;

async function logEvent(sessionId: string, type: string, payload: EventPayload = {}) {
  try {
    await push(ref(db, `sessions/${sessionId}/events`), {
      type,
      createdAt: serverTimestamp(),
      ...payload,
    });
  } catch (error) {
    console.error('Failed to log event', { sessionId, type, error });
  }
}

export async function listClips(): Promise<Clip[]> {
  // Static list mapped to bundled assets/audio
  return [
    {
      id: '1',
      title: 'Clip 1',
      filename: '/audio/tense/Clip 1 - Tense .mp3',
      durationSec: 30,
      valence: -0.1,
      arousal: 0.9,
      emotionTag: 'tense',
      useForShift: false,
      useForStay: true,
      sortOrder: 1,
    },
    {
      id: '2',
      title: 'Clip 2',
      filename: '/audio/tense/Clip 2 - Tense.mp3',
      durationSec: 30,
      valence: 0.2,
      arousal: 0.85,
      emotionTag: 'tense',
      useForShift: false,
      useForStay: true,
      sortOrder: 2,
    },
    {
      id: '3',
      title: 'Clip 3',
      filename: '/audio/tense/Clip 3 - Tense .mp3',
      durationSec: 30,
      valence: -0.2,
      arousal: 0.8,
      emotionTag: 'tense',
      useForShift: false,
      useForStay: true,
      sortOrder: 3,
    },
    {
      id: '4',
      title: 'Clip 4',
      filename: '/audio/tense/Clip 4 - Tense .mp3',
      durationSec: 30,
      valence: 0.1,
      arousal: 0.75,
      emotionTag: 'tense',
      useForShift: false,
      useForStay: true,
      sortOrder: 4,
    },
    {
      id: '5',
      title: 'Clip 3',
      filename: '/audio/calm/Clip 3 - Calm .mp3',
      durationSec: 30,
      valence: 0.3,
      arousal: -0.5,
      emotionTag: 'calm',
      useForShift: true,
      useForStay: true,
      sortOrder: 5,
    },
    {
      id: '6',
      title: 'Clip 4',
      filename: '/audio/calm/Clip 4 - Calm.mp3',
      durationSec: 30,
      valence: 0.4,
      arousal: -0.6,
      emotionTag: 'calm',
      useForShift: true,
      useForStay: true,
      sortOrder: 6,
    },
    {
      id: '7',
      title: 'Clip 5',
      filename: '/audio/calm/Clip 5 - Calm.mp3',
      durationSec: 30,
      valence: 0.2,
      arousal: -0.7,
      emotionTag: 'calm',
      useForShift: true,
      useForStay: true,
      sortOrder: 7,
    },
  ];
}

export async function getGuidanceTemplates(): Promise<GuidanceTemplate[]> {
  // TODO: Replace with actual Data Connect query
  // import { getGuidanceTemplates } from '../api/generated';
  // return getGuidanceTemplates();
  
  // Use default templates from guidance module
  const { defaultGuidanceTemplates } = await import('@/calculations/guidance.ts');
  return defaultGuidanceTemplates;
}

export async function createSession(data: {
  activity: string;
  energyLevel: number;
  stressLevel: number;
  baselineMood: number;
  baselineNote: string;
  deviceId: string | null;
  contextDurationMs?: number;
  baselineDurationMs?: number;
}): Promise<{ id: string; createdAt: Date }> {
  if (!data.deviceId) {
    throw new Error('AUTH_REQUIRED_FOR_SESSION_WRITE');
  }

  const sessionRef = push(ref(db, 'sessions'));
  const id = sessionRef.key ?? crypto.randomUUID();
  const createdAt = Date.now();

  const record = {
    status: 'in_progress' as const,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    deviceId: data.deviceId,
    context: {
      activity: data.activity,
      energy: data.energyLevel,
      stress: data.stressLevel,
      durationMs: data.contextDurationMs ?? null,
    },
    baseline: {
      mood: data.baselineMood,
      note: data.baselineNote,
      durationMs: data.baselineDurationMs ?? null,
    },
  };

  await set(sessionRef, record);
  await logEvent(id, 'session_created', {
    baselineMood: data.baselineMood,
    contextActivity: data.activity,
  });

  return { id, createdAt: new Date(createdAt) };
}

export async function addMoodCheckin(data: {
  sessionId: string;
  checkinType: string;
  sequenceIndex: number;
  clipIndex: number | null;
  silenceIndex: number | null;
  moodValue: number;
  durationMs?: number;
}): Promise<{ id: string; recordedAt: Date }> {
  const checkinRef = push(ref(db, `sessions/${data.sessionId}/checkins`));
  const id = checkinRef.key ?? crypto.randomUUID();
  const recordedAt = Date.now();

  await set(checkinRef, {
    ...data,
    recordedAt: serverTimestamp(),
    durationMs: data.durationMs ?? null,
  });

  await logEvent(data.sessionId, 'mood_checkin', {
    checkinType: data.checkinType,
    clipIndex: data.clipIndex,
    silenceIndex: data.silenceIndex,
    moodValue: data.moodValue,
    durationMs: data.durationMs ?? null,
  });

  return { id, recordedAt: new Date(recordedAt) };
}

export async function updateSessionChoice(
  sessionId: string,
  shiftChoice: string,
  durationMs?: number
): Promise<{ id: string; shiftChoice: string }> {
  await update(ref(db, `sessions/${sessionId}`), {
    shiftChoice,
    updatedAt: serverTimestamp(),
  });
  await logEvent(sessionId, 'shift_choice', { shiftChoice, durationMs: durationMs ?? null });
  return { id: sessionId, shiftChoice };
}

export async function completeSession(
  sessionId: string,
  data: {
    reflectionQ1: string | null;
    reflectionQ2: number | null;
    reflectionQ3: number | null;
    reflectionNote?: string | null;
    reflectionDurationMs?: number | null;
    reactionStrength: number | null;
    residualEffect: number | null;
    shiftEffect: number | null;
    durationMs?: number | null;
  }
): Promise<{ id: string; status: string; completedAt: Date }> {
  const completedAt = Date.now();

  await update(ref(db, `sessions/${sessionId}`), {
    status: 'completed',
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    reflections: {
      q1: data.reflectionQ1,
      q2: data.reflectionQ2,
      q3: data.reflectionQ3,
      note: data.reflectionNote ?? null,
      durationMs: data.reflectionDurationMs ?? null,
    },
    metrics: {
      reactionStrength: data.reactionStrength,
      residualEffect: data.residualEffect,
      shiftEffect: data.shiftEffect,
    },
    durationMs: data.durationMs ?? null,
  });

  await logEvent(sessionId, 'session_completed', {
    reflectionQ1: data.reflectionQ1,
    reflectionQ2: data.reflectionQ2,
    reflectionQ3: data.reflectionQ3,
    reflectionNote: data.reflectionNote,
  });

  return {
    id: sessionId,
    status: 'completed',
    completedAt: new Date(completedAt),
  };
}

export async function abandonSession(sessionId: string): Promise<{ id: string; status: string }> {
  await update(ref(db, `sessions/${sessionId}`), {
    status: 'abandoned',
    closedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logEvent(sessionId, 'session_abandoned');
  return { id: sessionId, status: 'abandoned' };
}
